import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { XIcon, TagIcon } from './icons';

interface TagModalProps {
    selectedCount: number;
    isOpen: boolean;
    onClose: () => void;
    onAddTags: (tags: string[]) => void;
}

const TagModal: React.FC<TagModalProps> = ({ selectedCount, isOpen, onClose, onAddTags }) => {
    const [tagInput, setTagInput] = useState('');
    const [tags, setTags] = useState<string[]>([]);

    if (!isOpen) return null;

    const handleAddTag = () => {
        const trimmedTag = tagInput.trim();
        if (trimmedTag && !tags.includes(trimmedTag)) {
            setTags([...tags, trimmedTag]);
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const handleSubmit = () => {
        if (tags.length > 0) {
            onAddTags(tags);
            setTags([]);
            setTagInput('');
            onClose();
        }
    };

    const handleCancel = () => {
        setTags([]);
        setTagInput('');
        onClose();
    };

    const handleCancelWithPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
        handleCancel();
    };

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" onClick={handleCancelWithPropagation}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="bg-action px-6 py-4 flex justify-between items-center text-white rounded-t-2xl">
                    <h3 className="font-bold flex items-center gap-2 text-lg">
                        <TagIcon className="w-5 h-5" />
                        Add Tags to {selectedCount} {selectedCount === 1 ? 'Submission' : 'Submissions'}
                    </h3>
                    <button onClick={handleCancel} className="hover:bg-white/20 p-1 rounded-full transition-colors">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-text-muted mb-2">Add Tags</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="flex-1 border border-app-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-action focus:ring-1 focus:ring-action transition-colors"
                                placeholder="Enter a tag and press Enter..."
                            />
                            <button
                                onClick={handleAddTag}
                                className="px-4 py-2 bg-action/10 text-action rounded-lg text-sm font-medium hover:bg-action/15 transition-colors"
                            >
                                Add
                            </button>
                        </div>
                        <p className="text-xs text-text-muted mt-1">Press Enter or click Add to add each tag</p>
                    </div>

                    {/* Tags Preview */}
                    {tags.length > 0 && (
                        <div className="bg-app-bg rounded-xl p-4 border border-app-border">
                            <p className="text-xs font-bold text-text-faint uppercase tracking-wider mb-3">Tags to Add</p>
                            <div className="flex flex-wrap gap-2">
                                {tags.map((tag, index) => (
                                    <div
                                        key={index}
                                        className="bg-action text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2"
                                    >
                                        <span>{tag}</span>
                                        <button
                                            onClick={() => handleRemoveTag(tag)}
                                            className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                                        >
                                            <XIcon className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Info */}
                    <div className="bg-status-info-bg rounded-lg p-3 border border-status-info">
                        <p className="text-xs text-status-info" style={{ textWrap: 'pretty' }}>
                            <strong>Note:</strong> Tags will be added to all {selectedCount} selected submission{selectedCount !== 1 ? 's' : ''}. Existing tags will be preserved.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-app-border flex justify-end gap-3 bg-app-bg rounded-b-2xl">
                    <button
                        onClick={handleCancel}
                        className="px-4 py-2 text-sm font-medium text-text-muted hover:bg-app-bg rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={tags.length === 0}
                        className="px-6 py-2 text-sm font-medium text-white bg-action hover:bg-action/90 rounded-lg active:scale-95 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Add {tags.length > 0 ? `${tags.length} Tag${tags.length !== 1 ? 's' : ''}` : 'Tags'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TagModal;
